import { computed, inject, Injectable, signal } from "@angular/core";
import { EventBody, EventType, FrontendPermissions, ITag, NotifyChannel, TagType } from "@vierkant-software/types__api";
import { filter, Subscription } from "rxjs";
import { signalMarkAsDirty } from "src/app/util/signal";
import { AppService } from "src/services/app.service";
import { groupBy } from "src/util/array";

type TAGS = Record<TagType, ITag[]>;

const CHANNEL = NotifyChannel.tag;
const EVENT_TYPE = EventType.tag;

@Injectable({providedIn: 'root'})
export class TagService {

    public readonly fetched = computed(() => this.ready());
    private readonly ready = signal(false);
    public readonly tags = signal<TAGS>({} as TAGS);
    public appService: AppService = inject(AppService);
    public readonly _hasCreatePermission = computed(() => this.appService.getFrontendPermission(FrontendPermissions.tag_create));
    private eventSubscription: Subscription;

    get hasCreatePermission() {
        return this._hasCreatePermission();
    }

    constructor(private app: AppService) {
        this.app.subscribeChannel(CHANNEL);
        this.eventSubscription = this.app.$events
            .pipe(filter(ev => ev.data.type === EVENT_TYPE))
            .subscribe(({data}: {data: Extract<EventBody, {type: EventType.tag}>}) => this.handleTagEvent(data.data));

        this.app.api.SettingsWorker.getAllTags().then(tags => {
            this.tags.set(groupBy(tags, "type") as TAGS);
            this.ready.set(true);
        }).catch(err => err.displayErrorToast?.());
    }

    public getTags(type: TagType) {
        return this.tags()[type] ??= [];
    }

    public async createTag(tag: ITag) {
        if (!this.hasCreatePermission) return undefined;
        const newTag = await this.app.api.SettingsWorker.createTag(tag.type, tag.value);
        const tags = (this.tags()[newTag.type] ??= []);
        const index = tags.findIndex(x => x.ID === newTag.ID);
        if (index < 0) tags.push(newTag);
        else tags[index] = newTag;
        signalMarkAsDirty(this.tags);
        return newTag;
    }

    /**
    * Load tags of a specific type from the backend.
    */
    public async fetchTags(type: TagType) {
        const tags = await this.app.api.SettingsWorker.getTags(type);
        this.tags()[type] = tags;
        signalMarkAsDirty(this.tags);
        return tags;
    }

    private handleTagEvent(tag: ITag & {toDelete?: true}) {
        if (tag.toDelete) return this.internalRemoveTag(tag);
        return this.internalUpsertTag(tag);
    }

    private internalUpsertTag(tag: ITag) {
        const tags = (this.tags()[tag.type] ??= []);
        const index = tags.findIndex(x => x.ID === tag.ID);
        if (index < 0) tags.push({ ...tag });
        else tags[index].value = tag.value;
        signalMarkAsDirty(this.tags);
    }

    private internalRemoveTag(tag: ITag) {
        const tags = (this.tags()[tag.type] ??= []);
        const index = tags.findIndex(x => x.ID === tag.ID);
        // might not be able to remove something which doesn't exist here
        if (index >= 0) tags.splice(index, 1);
        return signalMarkAsDirty(this.tags);
    }
}